import type React from "react";
import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Text } from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import { IconAlertCircle, IconArrowLeft } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { JobStatus, ReviewStatus } from "@models/job";
import { useJobResults } from "@hooks/useJobResults";
import { useErrorToast } from "@hooks/useErrorToast";
import JobResultsBody from "@features/JobResults/JobResultsBody";
import ItemRequestModal from "@features/JobResults/ItemRequestModal";
import JobResultsTopbar from "@features/JobResults/topbar/JobResultsTopbar";
import { useResultsKeyboardShortcuts } from "@hooks/jobResults/useResultsKeyboardShortcuts";
import { useJobDetails } from "@hooks/jobResults/useJobDetails";
import { useReviewActions } from "@hooks/jobResults/useReviewActions";
import { useBulkAccept } from "@hooks/jobResults/useBulkAccept";
import { useResultsExport } from "@hooks/jobResults/useResultsExport";
import { useResultsFilter } from "@hooks/jobResults/useResultsFilter";
import { useItemRequest } from "@hooks/jobResults/useItemRequest";
import { useRowFocus } from "@hooks/jobResults/useRowFocus";
import "./JobResultsPage.scss";

const JobResultsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();

  const { job, jobLoading, jobError, clearJobError, jobVersion, fetchJob } =
    useJobDetails(jobId);

  const showResults =
    job !== null &&
    (job.status === JobStatus.Running || job.status === JobStatus.Completed);

  const results = useJobResults(
    showResults ? (jobId ?? null) : null,
    jobVersion,
  );

  const review = useReviewActions(results, job?.pluginId);
  const bulk = useBulkAccept(results);
  const exporter = useResultsExport(job, results);
  const filter = useResultsFilter(results);
  const itemRequest = useItemRequest(jobId ?? "", results.rows);
  const rowFocus = useRowFocus();

  useErrorToast(
    jobError,
    t("jobResultsPage.errors.loadJobTitle"),
    clearJobError,
  );
  useErrorToast(results.error, t("mappingServicePage.main.resultsErrorTitle"));

  const goBackToService = useCallback((): void => {
    navigate(-1);
  }, [navigate]);

  useResultsKeyboardShortcuts({
    rows: filter.filteredRows,
    focusedRowIndex: rowFocus.focusedRowIndex,
    setFocusedRowIndex: rowFocus.setFocusedRowIndex,
    rowRefs: rowFocus.rowRefs,
    onAccept: review.handleAccept,
    onReject: review.handleReject,
    onPickAlternative: review.handlePickAlternative,
  });

  if (!jobId) {
    return (
      <Box className="job-results-page job-results-page--invalid">
        <Alert
          color="red"
          icon={<IconAlertCircle size={18} />}
          title={t("jobResultsPage.errors.invalidJobTitle")}
        >
          <Text size="sm">{t("jobResultsPage.errors.invalidJob")}</Text>
          <Button
            mt="md"
            variant="light"
            leftSection={<IconArrowLeft size={16} />}
            onClick={goBackToService}
          >
            {t("jobResultsPage.actions.backToService")}
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="job-results-page">
      <JobResultsTopbar
        job={job}
        jobId={jobId}
        jobLoading={jobLoading}
        hasResults={showResults}
        rowCount={results.rows.length}
        editsCount={results.edits.size}
        acceptedCount={filter.statusCounts[ReviewStatus.Accepted]}
        onBack={goBackToService}
        onRefresh={() => {
          void fetchJob();
          void results.refresh();
        }}
        onExport={exporter.handleExport}
        onExportExcel={() => void exporter.handleExportExcel()}
        exportingExcel={exporter.exportingExcel}
        bulkOpen={bulk.bulkOpen}
        onBulkOpenChange={bulk.setBulkOpen}
        bulkRunning={bulk.bulkRunning}
        bulkThreshold={bulk.bulkThreshold}
        onBulkThresholdChange={bulk.setBulkThreshold}
        bulkEligibleCount={bulk.bulkEligibleRows.length}
        bulkSummary={bulk.bulkSummary}
        onBulkCancel={() => {
          bulk.setBulkOpen(false);
          bulk.setBulkSummary(null);
        }}
        onBulkApply={() => void bulk.handleBulkAccept()}
        missingCount={itemRequest.missingCount}
        onRequestItems={() => void itemRequest.open()}
      />

      <ItemRequestModal
        opened={itemRequest.opened}
        loading={itemRequest.loading}
        sending={itemRequest.sending}
        preview={itemRequest.preview}
        onClose={itemRequest.close}
        onSend={(edited) => void itemRequest.send(edited)}
      />

      <JobResultsBody
        job={job}
        jobId={jobId}
        showResults={showResults}
        results={results}
        filteredRows={filter.filteredRows}
        statusFilter={filter.statusFilter}
        onStatusFilterChange={(next) => {
          filter.setStatusFilter(next);
          filter.setPriorityOnly(false);
          filter.setMissingOnly(false);
        }}
        statusCounts={filter.statusCounts}
        priorityOnly={filter.priorityOnly}
        onPriorityOnlyChange={(next) => {
          filter.setPriorityOnly(next);
          if (next) filter.setStatusFilter("all");
        }}
        priorityCount={filter.priorityCount}
        missingOnly={filter.missingOnly}
        onMissingOnlyChange={(next) => {
          filter.setMissingOnly(next);
          if (next) filter.setStatusFilter("all");
        }}
        missingCount={filter.missingCount}
        edits={results.edits}
        saveStates={results.saveStates}
        registerRowRef={rowFocus.registerRowRef}
        onAccept={review.handleAccept}
        onReject={review.handleReject}
        onPickAlternative={review.handlePickAlternative}
        onRecover={review.handleRecover}
        onRetrySave={results.retrySave}
        onRowRestored={(row) => results.applyServerRow(row)}
        onLoadMore={() => void results.loadMore()}
        onAddRow={review.handleAddRow}
      />
    </Box>
  );
};

export default JobResultsPage;
